/**
 * BreastTissueRestorationPlan.jsx — iter652
 * Section 5c "🌿 Breast Tissue Restoration Plan" — 7-step BTRE UI.
 *
 * Educational. NON-diagnostic, NON-prognostic. Lexicon enforced server-side.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useHealthEvent } from "@/utils/healthEvents";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Leaf, Sparkles, RefreshCw, Loader2, ShieldAlert, ShieldCheck, Target, TrendingUp,
  ClipboardCheck, Apple, Activity, Moon, HeartPulse, Pill, Scale, WineOff, Cigarette,
  Dna, ExternalLink, Utensils, BookOpen, ChevronDown, FlaskConical, Info,
  Sun, Sunrise, Sunset, Clock, Ban, Coffee,
} from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import PathwayMenuModal from "./PathwayMenuModal";
import WhatIfNisDilCalculator from "./WhatIfNisDilCalculator";

const TX = {
  bg: {
    title: "🌿 Breast Tissue Restoration Plan",
    subtitle:
      "Персонализиран план за оптимизация на тъканната микросреда. Базира се на BHI данни, лабораторни маркери и lifestyle фактори. Образователен модул — НЕ е диагностично или прогностично средство.",
    refresh: "Обнови плана",
    enrich: "✨ AI наратив",
    enriching: "Генерира…",
    cached: "от кеш",
    step1: "1. Текущо състояние на тъканта",
    step2: "2. Главни биологични двигатели (които може да променим)",
    step3: "3. Цели за тъканна оптимизация",
    step4: "4. Персонализирани числови цели",
    step5: "5. Очаквани промени в тъканта",
    step6: "6. Персонализирана пътна карта",
    step7: "7. Метрики за проследяване",
    step8: "8. Биологични процеси активирани от твоите диагнози",
    step8_sub: `Тези процеси идват само от структурираните ти диагнози (не от рисковите фактори). Виж пълния списък (диагнози + рискови фактори) в „Мои активирани пътища" на страницата Breast Health Intelligence.`,
    step8_empty: `Няма активирани пътища от диагнози — добави диагнози в „Вашите диагнози" по-горе или изчакай да ги извлечем от твоя здравен профил.`,
    step8_foods: "Топ храни (по брой поддържани ваши пътища)",
    step8_intr: "Интервенции",
    step8_prev: "Prevention фокус",
    step8_mod:  "Фактори за въздействие",
    step8_pw_lbl: "Ваши активни пътища",
    step8_support: "пътя",
    step8_reveal_pw: "Виж кои пътища",
    step8_hide_pw:   "Скрий",
    step8_reverse_title: "🔎 За всеки пътят — неговите храни и интервенции",
    step8_reverse_sub: "Обратен изглед: за всеки от вашите активни пътища показваме кои конкретни храни и интервенции го подпомагат. Така всяка препоръка е проследима до биологичния си механизъм.",
    step8_reverse_foods_lbl: "Храни",
    step8_reverse_intr_lbl: "Интервенции",
    step8_reverse_prev_lbl:  "Профилактика",
    step8_reverse_mod_lbl:   "Фактори за въздействие",
    step8_ctd_lbl:           "Био-активни съединения (CTD)",
    step8_ctd_help:          "Химични съединения статистически асоциирани с този пътят според CTD (обновявана ежемесечно).",
    step8_explain_hint:      "Кликни за 2-изреченско обяснение защо тази храна подпомага пътят",
    step8_explain_loading:   "Генерира обяснение…",
    step8_sources_footer:    "📚 Източници: KEGG (pathway ID-та) · CTD, ctdbase.org (chem→pathway) · AI обясненията ползват Claude Haiku. Образователно съдържание — НЕ диагностично.",
    step9: "9. Хранителни двигатели (NIS / DIL / UIS)",
    step9_sub: "NIS = Nutramilo Insulin Score (%), DIL = Dietary Insulin Load (g), UIS = Ultra-processed Intake Score. Изчислени от последните 14 дни от храна-лог. Влияят върху активацията на пътищата по-горе.",
    step9_empty: "Няма достатъчно данни от храна-лог (нужни са последните 14 дни).",
    step9_boosted: "Пътища, най-силно повлияни от текущата ви диета:",
    step10: "10. 🕒 Циркадна програма — какво, кога и защо",
    step10_sub: "Персонализирано разписание, което подрежда препоръчаните ви храни, интервенции и добавки в 5 времеви прозореца, базирано на хроно-нутриция (кортизолов ритъм, инсулинова чувствителност, мелатонинов пик).",
    step10_empty: "Няма достатъчно данни за построяване на циркадна програма — добавете диагнози, за да получите персонализирано разписание.",
    step10_biology: "Биология",
    step10_foods_lbl: "Храни",
    step10_avoid_lbl: "Избягвайте",
    step10_supps_lbl: "Добавки",
    step10_intr_lbl: "Интервенции",
    step10_with_fat: "с мазнина",
    step10_constraints_lbl: "🚦 Общи хроно-правила",
    step10_unassigned_lbl: "Без специфичен слот (гъвкави)",
    step10_pathways_lbl: "Свързани пътища",
    step10_chronotype_lbl: "Chronotype",
    step10_lark: "Чучулига (сутрешен)",
    step10_balanced: "Балансиран",
    step10_owl: "Сова (вечерен)",
    step10_chronotype_hint: "Промяната отмества всичките 5 прозорци с ±2 часа спрямо биологичния ви часовник.",
    step10_auto_detected: "🌙 Digital Twin засече Вашия ритъм",
    step10_auto_apply: "Приложи авто-препоръка",
    step10_auto_reject: "Не сега",
    step10_auto_evidence: "Доказателство",
    physician_pdf: "🩺 Свали клиничен PDF за лекар",
    physician_pdf_busy: "Генерира PDF…",
    step11: "11. 🍽️ Меню-препоръки — какво точно да ядете",
    step11_sub: "Курирани ястия за всеки прозорец от Циркадната програма, филтрирани по вашите активни пътища и хранителни индекси (NIS/DIL/UIS/GI/PRAL).",
    step11_ai_btn: "✨ Генерирай AI 7-дневно меню",
    step11_ai_busy: "AI генерира менюто (10-15 сек)…",
    step11_ai_regenerate: "🔄 Генерирай наново",
    step11_kcal: "kcal",
    step11_prep: "мин",
    step11_gi_low: "нисък GI",
    step11_gi_moderate: "среден GI",
    step11_gi_high: "висок GI",
    step11_nis_lbl: "NIS",
    step11_dil_lbl: "DIL",
    step11_overlap: "поддържани пътища",
    step11_recipe_link: "📖 Отвори в Симулатора на чинията",
    step11_reshuffle: "🔀 Друга препоръка",
    step11_reshuffling: "Обновява…",
    step11_low_nis_toggle: "⬇️ Приоритет: най-нисък NIS",
    step11_wrapped_toast: "Изчерпани са алтернативите — показвам от началото",
    step11_daily_over_dil: "⚠️ Дневен DIL надвишен",
    step11_daily_over_nis: "⚠️ Дневен NIS надвишен",
    step11_daily_safe: "✓ Дневни лимити в норма",
    step11_daily_ci: "14-дневен интервал (95% CI, Monte Carlo)",
    empty: "Зареждане…",
    current: "сега",
    target: "цел",
    impact_high: "висок ефект",
    impact_moderate: "умерен",
    impact_mild: "лек",
    impact_low: "нисък",
    modifiable: "който може да променим",
    forceRefresh: "Нов AI анализ",
    noAi: "Натиснете бутона за AI наратив, за да получите кратък персонализиран преглед.",
    timeoutMsg: "AI отговорът отнема повече от обичайното. Опитайте отново след минута.",
  },
  en: {
    title: "🌿 Breast Tissue Restoration Plan",
    subtitle:
      "Personalised tissue microenvironment optimisation plan. Built from BHI signals, lab markers and lifestyle factors. Educational module — NOT a diagnostic or prognostic device.",
    refresh: "Refresh plan",
    enrich: "✨ AI narrative",
    enriching: "Generating…",
    cached: "cached",
    step1: "1. Current breast tissue state",
    step2: "2. Main biological drivers (modifiable)",
    step3: "3. Tissue restoration targets",
    step4: "4. Personalised numerical goals",
    step5: "5. Expected tissue-level effects",
    step6: "6. Personalised intervention roadmap",
    step7: "7. Follow-up metrics",
    step8: "8. KEGG pathways activated by your diagnoses",
    step8_sub: "Personalised: these pathways are inferred from your structured diagnoses. Foods and interventions below are ranked by how many of YOUR activated pathways they support.",
    step8_empty: "No activated pathways — add diagnoses in «Your diagnoses» above to get personalised KEGG-driven guidance.",
    step8_foods: "Top foods (by count of your pathways they support)",
    step8_intr: "Interventions",
    step8_prev: "Prevention focus",
    step8_mod:  "Modifiable factors",
    step8_pw_lbl: "Your active pathways",
    step8_support: "pathways",
    step8_reveal_pw: "Show pathways",
    step8_hide_pw:   "Hide",
    step8_reverse_title: "🔎 Reverse view — each pathway & its foods",
    step8_reverse_sub: "For each of your activated pathways, see the concrete foods and interventions that support it. Every recommendation is traceable to its biological mechanism.",
    step8_reverse_foods_lbl: "Foods",
    step8_reverse_intr_lbl: "Interventions",
    step8_reverse_prev_lbl:  "Prevention focus",
    step8_reverse_mod_lbl:   "Modifiable factors",
    step8_ctd_lbl:           "Bioactive compounds (CTD)",
    step8_ctd_help:          "Chemical compounds statistically associated with this pathway per CTD (updated monthly).",
    step8_explain_hint:      "Click for a 2-sentence explanation why this food supports the pathway",
    step8_explain_loading:   "Generating explanation…",
    step8_sources_footer:    "📚 Sources: KEGG (pathway IDs) · CTD, ctdbase.org (chem→pathway) · AI explanations powered by Claude Haiku. Educational content — NOT diagnostic.",
    step9: "9. Dietary drivers (NIS / DIL / UIS)",
    step9_sub: "NIS = Nutramilo Insulin Score (%), DIL = Dietary Insulin Load (g), UIS = Ultra-processed Intake Score. Computed from the last 14 days of meal log. Modulates pathway activation above.",
    step9_empty: "Not enough meal-log data (last 14 days required).",
    step9_boosted: "Pathways most strongly boosted by your current diet:",
    step10: "10. 🕒 Circadian Program — what, when & why",
    step10_sub: "Personalised 5-window schedule for your foods, interventions and supplements, aligned to chrono-nutrition (cortisol rhythm, insulin sensitivity, melatonin onset).",
    step10_empty: "Not enough data to build a circadian program — add diagnoses to receive one.",
    step10_biology: "Biology",
    step10_foods_lbl: "Foods",
    step10_avoid_lbl: "Avoid",
    step10_supps_lbl: "Supplements",
    step10_intr_lbl: "Interventions",
    step10_with_fat: "with fat",
    step10_constraints_lbl: "🚦 Universal chrono-rules",
    step10_unassigned_lbl: "No specific slot (flexible)",
    step10_pathways_lbl: "Related pathways",
    step10_chronotype_lbl: "Chronotype",
    step10_lark: "Lark (early)",
    step10_balanced: "Balanced",
    step10_owl: "Owl (late)",
    step10_chronotype_hint: "Shifts all 5 windows by ±2h to match your biological clock.",
    step10_auto_detected: "🌙 Your Digital Twin detected your rhythm",
    step10_auto_apply: "Apply auto-suggestion",
    step10_auto_reject: "Not now",
    step10_auto_evidence: "Evidence",
    physician_pdf: "🩺 Download physician handoff PDF",
    physician_pdf_busy: "Generating PDF…",
    step11: "11. 🍽️ Menu suggestions — exactly what to eat",
    step11_sub: "Curated meals for each circadian window, filtered by your active pathways and dietary indices (NIS/DIL/UIS/GI/PRAL).",
    step11_ai_btn: "✨ Generate AI 7-day menu",
    step11_ai_busy: "AI generating menu (10-15s)…",
    step11_ai_regenerate: "🔄 Regenerate",
    step11_kcal: "kcal",
    step11_prep: "min",
    step11_gi_low: "low GI",
    step11_gi_moderate: "moderate GI",
    step11_gi_high: "high GI",
    step11_overlap: "supported pathways",
    step11_recipe_link: "📖 Open in Plate Simulator",
    step11_reshuffle: "🔀 Reshuffle",
    step11_reshuffling: "Reshuffling…",
    step11_low_nis_toggle: "⬇️ Prefer lowest NIS",
    step11_wrapped_toast: "Ran out of alternatives — restarting from the top",
    step11_daily_over_dil: "⚠️ Daily DIL exceeded",
    step11_daily_over_nis: "⚠️ Daily NIS exceeded",
    step11_daily_safe: "✓ Daily limits within range",
    step11_daily_ci: "14-day interval (95% CI, Monte Carlo)",
    empty: "Loading…",
    current: "now",
    target: "target",
    impact_high: "high impact",
    impact_moderate: "moderate",
    impact_mild: "mild",
    impact_low: "low",
    modifiable: "modifiable",
    forceRefresh: "New AI analysis",
    noAi: "Click 'AI narrative' for a short personalised summary.",
    timeoutMsg: "AI is taking longer than usual. Try again in a minute.",
  },
  es: {
    title: "🌿 Plan de Restauración del Tejido Mamario",
    subtitle:
      "Plan personalizado para optimizar el microambiente tisular. Basado en señales BHI, marcadores de laboratorio y estilo de vida. Módulo educativo — NO es un dispositivo diagnóstico o pronóstico.",
    refresh: "Actualizar plan",
    enrich: "✨ Narrativa IA",
    enriching: "Generando…",
    cached: "en caché",
    step1: "1. Estado actual del tejido",
    step2: "2. Principales conductores biológicos (modificables)",
    step3: "3. Objetivos de restauración",
    step4: "4. Objetivos numéricos personalizados",
    step5: "5. Efectos esperados a nivel tisular",
    step6: "6. Hoja de ruta de intervenciones",
    step7: "7. Métricas de seguimiento",
    step8: "8. Vías KEGG activadas por sus diagnósticos",
    step8_sub: "Personalizado: estas vías se calculan a partir de sus diagnósticos estructurados. Alimentos e intervenciones abajo están ordenados por cuántas de SUS vías activas soportan.",
    step8_empty: "Sin vías activadas — añada diagnósticos en «Sus diagnósticos» arriba.",
    step8_foods: "Top alimentos (por vías soportadas)",
    step8_intr: "Intervenciones",
    step8_prev: "Enfoque de prevención",
    step8_mod:  "Factores modificables",
    step8_pw_lbl: "Sus vías activas",
    step8_support: "vías",
    step8_reveal_pw: "Ver vías",
    step8_hide_pw:   "Ocultar",
    step8_reverse_title: "🔎 Vista inversa — cada vía y sus alimentos",
    step8_reverse_sub: "Para cada una de sus vías activas, vea los alimentos e intervenciones concretos que la sustentan.",
    step8_reverse_foods_lbl: "Alimentos",
    step8_reverse_intr_lbl: "Intervenciones",
    step8_reverse_prev_lbl:  "Prevención",
    step8_reverse_mod_lbl:   "Factores modificables",
    step8_ctd_lbl:           "Compuestos bioactivos (CTD)",
    step8_ctd_help:          "Compuestos químicos estadísticamente asociados con esta vía según CTD.",
    step8_explain_hint:      "Clic para explicación",
    step8_explain_loading:   "Generando…",
    step8_sources_footer:    "📚 Fuentes: KEGG · CTD (ctdbase.org) · NCCN/AGO/ESMO. Educativo — NO diagnóstico.",
    step9: "9. Motores dietéticos (NIS / DIL / UIS)",
    step9_sub: "NIS/DIL/UIS calculados de los últimos 14 días de registro de comidas.",
    step9_empty: "Datos insuficientes en el registro (últimos 14 días).",
    step9_boosted: "Vías más impulsadas por su dieta actual:",
    step10: "10. 🕒 Programa Circadiano — qué, cuándo y por qué",
    step10_sub: "Programa personalizado de 5 ventanas para alimentos, intervenciones y suplementos, alineado con la crononutrición.",
    step10_empty: "Datos insuficientes para el programa circadiano.",
    step10_biology: "Biología",
    step10_foods_lbl: "Alimentos",
    step10_avoid_lbl: "Evitar",
    step10_supps_lbl: "Suplementos",
    step10_intr_lbl: "Intervenciones",
    step10_with_fat: "con grasa",
    step10_constraints_lbl: "🚦 Reglas universales",
    step10_unassigned_lbl: "Sin franja específica",
    step10_pathways_lbl: "Vías asociadas",
    step10_chronotype_lbl: "Cronotipo",
    step10_lark: "Alondra",
    step10_balanced: "Balanceado",
    step10_owl: "Búho",
    step10_chronotype_hint: "Desplaza las 5 ventanas ±2h.",
    step10_auto_detected: "🌙 Su Digital Twin detectó su ritmo",
    step10_auto_apply: "Aplicar sugerencia",
    step10_auto_reject: "Ahora no",
    step10_auto_evidence: "Evidencia",
    physician_pdf: "🩺 Descargar PDF para médico",
    physician_pdf_busy: "Generando PDF…",
    empty: "Cargando…",
    current: "actual",
    target: "objetivo",
    impact_high: "alto impacto",
    impact_moderate: "moderado",
    impact_mild: "leve",
    impact_low: "bajo",
    modifiable: "modificable",
    forceRefresh: "Nuevo análisis IA",
    noAi: "Pulse 'Narrativa IA' para un resumen personalizado corto.",
    timeoutMsg: "La IA está tardando más de lo habitual. Inténtelo en un minuto.",
  },
};

const GROUP_ICONS = {
  Nutrition: Apple,
  "Physical Activity": Activity,
  Sleep: Moon,
  "Stress Management": HeartPulse,
  Supplements: Pill,
  "Weight Optimization": Scale,
  "Alcohol Reduction": WineOff,
  "Smoking Cessation": Cigarette,
};

const GROUP_LABEL = {
  bg: {
    Nutrition: "Хранене",
    "Physical Activity": "Физическа активност",
    Sleep: "Сън",
    "Stress Management": "Управление на стреса",
    Supplements: "Хранителни добавки",
    "Weight Optimization": "Оптимизация на теглото",
    "Alcohol Reduction": "Намаляване на алкохол",
    "Smoking Cessation": "Спиране на тютюнопушене",
  },
  en: {
    Nutrition: "Nutrition",
    "Physical Activity": "Physical Activity",
    Sleep: "Sleep",
    "Stress Management": "Stress Management",
    Supplements: "Supplements",
    "Weight Optimization": "Weight Optimization",
    "Alcohol Reduction": "Alcohol Reduction",
    "Smoking Cessation": "Smoking Cessation",
  },
  es: {
    Nutrition: "Nutrición",
    "Physical Activity": "Actividad física",
    Sleep: "Sueño",
    "Stress Management": "Gestión del estrés",
    Supplements: "Suplementos",
    "Weight Optimization": "Optimización del peso",
    "Alcohol Reduction": "Reducción de alcohol",
    "Smoking Cessation": "Dejar de fumar",
  },
};

const IMPACT_STYLES = {
  high:     "bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300",
  moderate: "bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-300",
  mild:     "bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300",
  low:      "bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300",
};

export default function BreastTissueRestorationPlan({ snapshot, clinical, labs, conditions, lang = "bg" }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const t = TX[lang] || TX.bg;
  const groupLabels = GROUP_LABEL[lang] || GROUP_LABEL.bg;

  // iter866 — What-If NIS/DIL calculator popover (Step 11 badges).
  const [whatIfMealId, setWhatIfMealId] = useState(null);

  // iter867 — Step 11 reshuffle state.
  const [slotOverrides, setSlotOverrides] = useState({});  // { slot: meals[] }
  const [reshufflingSlot, setReshufflingSlot] = useState(null);
  const [lowNisMode, setLowNisMode] = useState(() => {
    try { return localStorage.getItem("btre.step11.lowNisMode") === "1"; }
    catch { return false; }
  });

  // iter869 — Today's cumulative NIS/DIL + 14-day 95% CI (Monte Carlo).
  // iter870 — Also includes per-slot CI breakdown.
  const [dailyMetrics, setDailyMetrics] = useState(null);

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [enrich, setEnrich] = useState(null);
  const [enrichLoading, setEnrichLoading] = useState(false);
  // iter834 — Recipe deep-link from Step 8 food chips.
  const [recipeFocusFood, setRecipeFocusFood] = useState(null);
  // iter860 — Dynamic circadian program: chronotype + physician PDF export.
  const [chronotype, setChronotype] = useState(() => {
    try { return localStorage.getItem("btre.chronotype") || "balanced"; }
    catch { return "balanced"; }
  });
  const [pdfBusy, setPdfBusy] = useState(false);
  // iter861 — Auto-detect chronotype from Digital Twin sleep_logs.
  // Fetch once on mount; render dismissible banner when confidence ≥ moderate
  // and detected value differs from current selection.
  const [autoChrono, setAutoChrono] = useState(null);
  const [autoDismissed, setAutoDismissed] = useState(() => {
    try { return sessionStorage.getItem("btre.chronotype.autoDismissed") === "1"; }
    catch { return false; }
  });
  // iter863 — AI 7-day menu state (Step 11)
  const [weekMenu, setWeekMenu] = useState(null);
  const [weekMenuBusy, setWeekMenuBusy] = useState(false);
  const [weekMenuErr, setWeekMenuErr] = useState(null);

  const computePlan = useCallback(async () => {
    if (!snapshot) return;
    setLoading(true);
    setErr(null);
    try {
      // iter832 — Only send conditions/clinical/labs when non-empty so the
      // backend can autoload from PatientHealthContext when the parent
      // page did not pre-fetch them. Passing [] would OVERRIDE autoload
      // (backend checks `is None`), silently killing the KEGG-pathway
      // personalisation in Step 8/9.
      const body = { bhi_snapshot: snapshot, lang, chronotype };
      if (Array.isArray(conditions) && conditions.length > 0) body.conditions = conditions;
      if (clinical && Object.keys(clinical).length > 0) body.clinical = clinical;
      if (labs && Object.keys(labs).length > 0) body.labs = labs;
      const { data } = await axios.post(`${API}/bhi/tissue-restoration/preview`, body);
      setPlan(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [API, snapshot, clinical, labs, conditions, lang, chronotype]);

  useEffect(() => {
    if (snapshot) computePlan();
  }, [snapshot, computePlan]);

  // Persist chronotype and trigger re-compute when it changes.
  useEffect(() => {
    try { localStorage.setItem("btre.chronotype", chronotype); } catch (_e) { /* noop */ }
  }, [chronotype]);

  // iter869 — Fetch today's cumulative NIS/DIL + 14-day CI on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/bhi/nis-dil-daily?window_days=14&with_ci=true&with_slot_ci=true`
        );
        if (!cancelled) setDailyMetrics(data || null);
      } catch (_e) {
        if (!cancelled) setDailyMetrics(null);
      }
    })();
    return () => { cancelled = true; };
  }, [API]);


  // iter861 — Fetch auto-detected chronotype once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/bhi/tissue-restoration/detect-chronotype`);
        if (!cancelled) setAutoChrono(data || null);
      } catch (_e) {
        if (!cancelled) setAutoChrono(null);
      }
    })();
    return () => { cancelled = true; };
  }, [API]);

  const applyAutoChrono = useCallback(() => {
    if (autoChrono?.detected) {
      setChronotype(autoChrono.detected);
    }
  }, [autoChrono]);

  const dismissAutoChrono = useCallback(() => {
    setAutoDismissed(true);
    try { sessionStorage.setItem("btre.chronotype.autoDismissed", "1"); } catch (_e) { /* noop */ }
  }, []);

  const generateWeekMenu = useCallback(async () => {
    if (weekMenuBusy) return;
    setWeekMenuBusy(true);
    setWeekMenuErr(null);
    try {
      const { data } = await axios.post(
        `${API}/bhi/tissue-restoration/week-menu`,
        { chronotype, lang },
        { timeout: 60000 },
      );
      setWeekMenu(data);
    } catch (e) {
      setWeekMenuErr(e?.response?.data?.detail || e.message || "AI menu failed");
    } finally {
      setWeekMenuBusy(false);
    }
  }, [API, chronotype, lang, weekMenuBusy]);

  // iter867 — Step 11: reshuffle meals for one slot with exclude + sort mode.
  const reshuffleSlot = useCallback(async (slot) => {
    if (reshufflingSlot) return;
    setReshufflingSlot(slot);
    try {
      const s11 = plan?.step11_menu_suggestions || {};
      const activePathways = (s11.active_pathways) || [];
      const indices = s11.indices || {};
      const currentMeals = slotOverrides[slot] || (s11.per_slot || {})[slot] || [];
      const excludeIds = currentMeals.map(m => m.id);
      const { data } = await axios.post(
        `${API}/bhi/step11-reshuffle`,
        {
          slot,
          active_pathways: activePathways,
          indices,
          exclude_ids: excludeIds,
          sort_mode: lowNisMode ? "low_nis" : "balanced",
          top_n: currentMeals.length || 4,
        },
        { timeout: 15000 }
      );
      const meals = data?.meals || [];
      if (meals.length > 0) {
        setSlotOverrides(prev => ({ ...prev, [slot]: meals }));
      }
    } catch (e) {
      // silent failure — keep current suggestions
    } finally {
      setReshufflingSlot(null);
    }
  }, [API, plan, slotOverrides, lowNisMode, reshufflingSlot]);

  // iter867 — Toggle low-NIS mode; persists in localStorage. When flipped,
  // clears slotOverrides so the next render pulls the plan default (which
  // may be re-fetched with the new mode).
  const toggleLowNisMode = useCallback(() => {
    setLowNisMode(prev => {
      const next = !prev;
      try { localStorage.setItem("btre.step11.lowNisMode", next ? "1" : "0"); } catch { /* localStorage unavailable */ }
      setSlotOverrides({});
      return next;
    });
  }, []);


  const downloadPhysicianPdf = useCallback(async () => {
    if (pdfBusy) return;
    setPdfBusy(true);
    try {
      const resp = await axios.post(
        `${API}/bhi/tissue-restoration/physician-pdf`,
        { bhi_snapshot: snapshot, chronotype, lang },
        { responseType: "blob", timeout: 60000 },
      );
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      const today = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `nutramilo_bhi_handoff_${today}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "PDF download failed");
    } finally {
      setPdfBusy(false);
    }
  }, [API, snapshot, chronotype, lang, pdfBusy]);

  // iter810 — Live re-compute when the patient adds/removes a structured
  // condition. BTRE Step 1 (current state) + Step 6 (roadmap) reflect
  // diagnoses through condition_canonical_bridge, so we force_refresh.
  useHealthEvent("conditions-changed", () => { computePlan(true); });

  const runEnrich = async (force = false) => {
    if (!plan) return;
    setEnrichLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/bhi/tissue-restoration/enrich`,
        { plan, lang, force_refresh: force },
        { timeout: 90000 },
      );
      setEnrich(data);
    } catch (e) {
      const status = e?.response?.status;
      const isTimeout = status === 502 || status === 504 || e?.code === "ECONNABORTED";
      setEnrich({
        error: isTimeout ? t.timeoutMsg : (e?.response?.data?.detail || e.message),
      });
    } finally {
      setEnrichLoading(false);
    }
  };

  const cardCls = `rounded-2xl border ${
    isDark ? "bg-[hsl(222,47%,11%)] border-[hsl(223,47%,18%)]" : "bg-white border-gray-200"
  }`;

  return (
    <div className="space-y-5" data-testid="btre-plan">
      {/* Header */}
      <div className={`${cardCls} p-5`}>
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shrink-0">
            <Leaf className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-extrabold bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent">
              {t.title}
            </h3>
            <p className={`text-xs mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>{t.subtitle}</p>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            onClick={computePlan}
            disabled={loading}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300 hover:opacity-90 flex items-center gap-1.5"
            data-testid="btre-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> {t.refresh}
          </button>
          <button
            onClick={() => runEnrich(false)}
            disabled={enrichLoading || !plan}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:opacity-90 flex items-center gap-1.5 disabled:opacity-50"
            data-testid="btre-enrich-btn"
          >
            {enrichLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {enrichLoading ? t.enriching : t.enrich}
          </button>
        </div>
      </div>

      {/* Disclaimer */}
      {plan?.disclaimer && (
        <div
          className={`p-3 rounded-xl border-l-4 flex items-start gap-2 ${
            isDark ? "bg-amber-500/10 border-amber-500" : "bg-amber-50 border-amber-500"
          }`}
          data-testid="btre-disclaimer"
        >
          <ShieldAlert className={`w-4 h-4 shrink-0 mt-0.5 ${isDark ? "text-amber-300" : "text-amber-600"}`} />
          <p className={`text-[11px] leading-relaxed ${isDark ? "text-amber-200" : "text-amber-700"}`}>
            {plan.disclaimer}
          </p>
        </div>
      )}

      {loading && (
        <div className="text-center py-10">
          <Loader2 className="w-6 h-6 animate-spin mx-auto opacity-50" />
          <p className="mt-2 text-xs opacity-70">{t.empty}</p>
        </div>
      )}
      {err && (
        <div className="p-3 rounded-lg bg-red-500/10 text-red-500 text-sm" data-testid="btre-error">{err}</div>
      )}

      {plan && !loading && (
        <>
          {/* STEP 1 — Current state */}
          <SectionBlock t={t.step1} cardCls={cardCls} testid="btre-step1">
            <ul className="space-y-1.5">
              {(plan.step1_current_state || []).map((s, i) => (
                <li key={i} className="text-sm flex items-start gap-2">
                  <ClipboardCheck className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </SectionBlock>

          {/* STEP 2 — Drivers */}
          <SectionBlock t={t.step2} cardCls={cardCls} testid="btre-step2">
            <div className="grid sm:grid-cols-2 gap-2">
              {(plan.step2_main_drivers || []).map((d, i) => (
                <motion.div
                  key={i}
                  layout
                  className={`p-3 rounded-xl border ${isDark ? "border-white/10" : "border-gray-100"} bg-black/[0.02] dark:bg-white/[0.02]`}
                  data-testid={`btre-driver-${d.driver_id}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="font-semibold text-sm">{d.driver}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${IMPACT_STYLES[d.impact] || IMPACT_STYLES.low}`}>
                      {t[`impact_${d.impact}`] || d.impact}
                    </span>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-black/10 dark:bg-white/10 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-500" style={{ width: `${Math.min(100, d.burden)}%` }} />
                  </div>
                  <div className="flex items-center justify-between text-[10px] opacity-60 mt-1">
                    <span>burden: {d.burden}</span>
                    {d.modifiable && <span className="text-emerald-600 dark:text-emerald-400">✓ {t.modifiable}</span>}
                  </div>
                </motion.div>
              ))}
            </div>
          </SectionBlock>

          {/* STEP 3 — Targets */}
          <SectionBlock t={t.step3} cardCls={cardCls} testid="btre-step3">
            <div className="flex flex-wrap gap-1.5">
              {(plan.step3_restoration_targets || []).map((tg, i) => (
                <span key={i} className="px-2.5 py-1 rounded-full text-xs bg-teal-100 text-teal-700 dark:bg-teal-500/20 dark:text-teal-300 flex items-center gap-1">
                  <Target className="w-3 h-3" /> {tg}
                </span>
              ))}
            </div>
          </SectionBlock>

          {/* STEP 4 — Goals */}
          <SectionBlock t={t.step4} cardCls={cardCls} testid="btre-step4">
            <div className="grid sm:grid-cols-2 gap-3">
              {Object.entries(plan.step4_personalized_goals || {}).map(([k, v]) => (
                <div key={k} className={`p-3 rounded-xl border ${isDark ? "border-white/10" : "border-gray-100"}`}>
                  <div className="text-[11px] uppercase opacity-60 font-bold">{k.replace(/_/g, " ")}</div>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="text-lg font-extrabold text-orange-500">{v.current}</span>
                    <span className="text-xs opacity-50">{t.current}</span>
                    <span className="opacity-30">→</span>
                    <span className="text-lg font-extrabold text-emerald-500">{v.target}</span>
                    <span className="text-xs opacity-50">{t.target}</span>
                  </div>
                  <div className="mt-2 h-1.5 w-full rounded-full bg-black/10 dark:bg-white/10 overflow-hidden">
                    <div className="h-full bg-emerald-500" style={{ width: `${Math.max(0, Math.min(100, 100 - v.target))}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </SectionBlock>

          {/* STEP 5 — Effects */}
          <SectionBlock t={t.step5} cardCls={cardCls} testid="btre-step5">
            <ul className="grid sm:grid-cols-2 gap-1.5">
              {(plan.step5_expected_effects || []).map((e, i) => (
                <li key={i} className="text-sm flex items-start gap-2">
                  <TrendingUp className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                  <span>{e}</span>
                </li>
              ))}
            </ul>
          </SectionBlock>

          {/* STEP 6 — Roadmap */}
          <SectionBlock t={t.step6} cardCls={cardCls} testid="btre-step6">
            {/* iter917 — Evidence-based badge (hide when no evidence) */}
            {(() => {
              const meta = plan.step6_evidence_meta;
              if (!meta || !meta.evidence_based_count) return null;
              const downloadPdf = async () => {
                try {
                  const token = localStorage.getItem("token") || sessionStorage.getItem("nutramilo_access_token") || "";
                  const res = await axios.post(
                    `${API}/bhi/evidence/pdf`,
                    { bhi_snapshot: plan.step1_input_snapshot?.bhi_snapshot || {} },
                    { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" }
                  );
                  const url = URL.createObjectURL(res.data);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `nutramilo-evidence-${new Date().toISOString().slice(0,10)}.pdf`;
                  a.click();
                  setTimeout(() => URL.revokeObjectURL(url), 60_000);
                } catch { /* silent */ }
              };
              return (
                <div
                  className={`mb-3 px-3 py-2 rounded-lg border text-[11px] flex items-start gap-2 ${
                    isDark ? "bg-emerald-950/40 border-emerald-800 text-emerald-200" : "bg-emerald-50 border-emerald-200 text-emerald-800"
                  }`}
                  data-testid="btre-step6-evidence-badge"
                >
                  <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <div className="font-bold">
                      {meta.evidence_based_count} evidence-based препоръки
                    </div>
                    <div className="opacity-80">
                      Източници: {(meta.evidence_sources || []).join(" · ")}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={downloadPdf}
                    data-testid="btre-evidence-pdf-btn"
                    className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-md ${
                      isDark ? "bg-emerald-800 hover:bg-emerald-700 text-emerald-100"
                             : "bg-emerald-600 hover:bg-emerald-700 text-white"
                    }`}
                    title="Изтегли PDF за лекар — clinician-ready one-pager"
                  >
                    <BookOpen className="w-3 h-3" />
                    PDF за лекар
                  </button>
                </div>
              );
            })()}
            <div className="grid md:grid-cols-2 gap-3">
              {Object.entries(plan.step6_intervention_roadmap || {}).map(([group, items]) => {
                const Icon = GROUP_ICONS[group] || ClipboardCheck;
                return (
                  <div key={group} className={`p-3 rounded-xl border ${isDark ? "border-white/10" : "border-gray-100"}`} data-testid={`btre-roadmap-${group.toLowerCase().replace(/\s+/g, '-')}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="w-4 h-4 text-emerald-500" />
                      <span className="font-semibold text-xs">{groupLabels[group] || group}</span>
                    </div>
                    <ul className="space-y-1.5">
                      {items.map((it, i) => (
                        <RoadmapItem key={i} item={it} isDark={isDark} />
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </SectionBlock>

          {/* STEP 7 — Follow-up (iter1022 — semantic-aware rendering) */}
          <SectionBlock t={t.step7} cardCls={cardCls} testid="btre-step7">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {(plan.step7_followup_metrics || []).map((m) => {
                // iter1022 — differentiate "0 = optimum burden" from "— = no data"
                // and color-band per direction.
                const v = m.current;
                const isNull = v == null;
                const rounded = isNull ? null : Math.round(v);
                // Metric direction: burden (lower=better) vs. score (higher=better) vs. years-diff
                const burdenKeys = new Set(["ecm_pressure", "density_burden", "igf1_environment", "inflammation_environment", "insulin_environment"]);
                const scoreKeys = new Set(["breast_metabolic_score"]);
                const yearKeys = new Set(["tissue_age"]);
                let band = "neutral";
                let tag = "";
                if (isNull) {
                  band = "empty"; tag = "недостатъчно данни";
                } else if (burdenKeys.has(m.key)) {
                  if (rounded <= 20) { band = "optimal";  tag = "оптимално"; }
                  else if (rounded <= 40) { band = "good"; tag = "добро"; }
                  else if (rounded <= 60) { band = "moderate"; tag = "умерено"; }
                  else if (rounded <= 80) { band = "high"; tag = "високо"; }
                  else { band = "critical"; tag = "критично"; }
                } else if (scoreKeys.has(m.key)) {
                  if (rounded >= 80) { band = "optimal";  tag = "отлично"; }
                  else if (rounded >= 60) { band = "good"; tag = "добро"; }
                  else if (rounded >= 40) { band = "moderate"; tag = "средно"; }
                  else if (rounded >= 20) { band = "high"; tag = "слабо"; }
                  else { band = "critical"; tag = "критично"; }
                } else if (yearKeys.has(m.key)) {
                  // tissue_age is a years figure — no band, just a "г." suffix.
                  tag = "г.";
                }
                const bandCls = {
                  optimal: isDark ? "text-emerald-300" : "text-emerald-700",
                  good: isDark ? "text-lime-300" : "text-lime-700",
                  moderate: isDark ? "text-amber-300" : "text-amber-700",
                  high: isDark ? "text-orange-300" : "text-orange-700",
                  critical: isDark ? "text-red-300" : "text-red-700",
                  empty: isDark ? "text-slate-500" : "text-slate-400",
                  neutral: isDark ? "text-white" : "text-slate-900",
                }[band];
                const chipCls = {
                  optimal: isDark ? "bg-emerald-500/15 text-emerald-300" : "bg-emerald-100 text-emerald-800",
                  good: isDark ? "bg-lime-500/15 text-lime-300" : "bg-lime-100 text-lime-800",
                  moderate: isDark ? "bg-amber-500/15 text-amber-300" : "bg-amber-100 text-amber-800",
                  high: isDark ? "bg-orange-500/15 text-orange-300" : "bg-orange-100 text-orange-800",
                  critical: isDark ? "bg-red-500/15 text-red-300" : "bg-red-100 text-red-800",
                  empty: isDark ? "bg-white/5 text-slate-500 italic" : "bg-slate-100 text-slate-500 italic",
                  neutral: isDark ? "bg-white/5 text-slate-300" : "bg-slate-100 text-slate-600",
                }[band];
                return (
                  <div key={m.key}
                    data-testid={`btre-followup-${m.key}`}
                    className={`p-3 rounded-xl border ${isDark ? "border-white/10" : "border-gray-100"} text-center`}
                  >
                    <div className="text-[10px] uppercase opacity-60 leading-tight">{m.label}</div>
                    <div className={`text-lg font-extrabold mt-1 ${bandCls}`}>
                      {isNull ? "—" : rounded}
                    </div>
                    {tag && (
                      <div className={`mt-1 inline-block px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide ${chipCls}`}>
                        {tag}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </SectionBlock>

          {/* STEP 8 — Pathway-driven personalisation (iter832) */}
          <SectionBlock t={t.step8} cardCls={cardCls} testid="btre-step8">
            <p className={`text-[11px] mb-3 leading-relaxed ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {t.step8_sub}
            </p>
            {(() => {
              const s8 = plan.step8_pathway_driven_actions || {};
              const pwMeta = s8.activated_pathways_meta || [];
              if (pwMeta.length === 0) {
                return (
                  <div className={`text-xs p-3 rounded-lg italic ${isDark ? "bg-slate-800/40 text-slate-400" : "bg-slate-50 text-slate-500"}`} data-testid="btre-step8-empty">
                    {t.step8_empty}
                  </div>
                );
              }
              return (
                <div className="space-y-4">
                  {/* KEGG pathway chips */}
                  <div>
                    <div className={`text-[11px] uppercase font-bold tracking-widest mb-1.5 flex items-center gap-1.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                      <Dna className="w-3.5 h-3.5" /> {t.step8_pw_lbl} · {pwMeta.length}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {pwMeta.map((p) => (
                        <a
                          key={p.id}
                          href={p.kegg ? `https://www.kegg.jp/pathway/${p.kegg}` : "#"}
                          target={p.kegg ? "_blank" : undefined}
                          rel="noopener noreferrer"
                          title={p.kegg ? `KEGG: ${p.kegg} — отвори в KEGG` : p.id}
                          data-testid={`btre-step8-pw-${p.id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-indigo-100 text-indigo-800 dark:bg-indigo-500/20 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-500/30 transition-colors"
                        >
                          {p.name_bg}
                          {p.kegg && <ExternalLink className="w-2.5 h-2.5 opacity-60" />}
                        </a>
                      ))}
                    </div>
                  </div>

                  {/* Top foods with pathway_support badge + recipe deep-link (iter834) + pathway provenance (iter857) */}
                  {(s8.foods || []).length > 0 && (
                    <div>
                      <div className={`text-[11px] uppercase font-bold tracking-widest mb-1.5 flex items-center gap-1.5 ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>
                        <Utensils className="w-3.5 h-3.5" /> {t.step8_foods}
                      </div>
                      <div className="flex flex-col gap-1.5" data-testid="btre-step8-foods">
                        {(s8.foods || []).map((f, i) => (
                          <FoodChipExpandable
                            key={i}
                            food={f}
                            index={i}
                            isDark={isDark}
                            revealLabel={t.step8_reveal_pw}
                            hideLabel={t.step8_hide_pw}
                            onOpenRecipes={() => setRecipeFocusFood(f.item)}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Interventions with expandable pathway provenance */}
                  {(s8.interventions || []).length > 0 && (
                    <div>
                      <div className={`text-[11px] uppercase font-bold tracking-widest mb-1.5 flex items-center gap-1.5 ${isDark ? "text-teal-400" : "text-teal-600"}`}>
                        <Activity className="w-3.5 h-3.5" /> {t.step8_intr}
                      </div>
                      <div className="flex flex-col gap-1.5" data-testid="btre-step8-interventions">
                        {(s8.interventions || []).map((iv, i) => (
                          <InterventionChipExpandable
                            key={i}
                            item={iv}
                            index={i}
                            isDark={isDark}
                            revealLabel={t.step8_reveal_pw}
                            hideLabel={t.step8_hide_pw}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Prevention focus + Modifiable factors — 2-col */}
                  <div className="grid sm:grid-cols-2 gap-3">
                    {(s8.prevention_focus || []).length > 0 && (
                      <div>
                        <div className={`text-[11px] uppercase font-bold tracking-widest mb-1.5 ${isDark ? "text-purple-400" : "text-purple-600"}`}>
                          {t.step8_prev}
                        </div>
                        <ul className="space-y-1">
                          {(s8.prevention_focus || []).map((p, i) => (
                            <li key={i} className="text-[12px] flex items-center justify-between gap-2">
                              <span className="flex items-start gap-1.5"><span className="text-purple-500">▸</span>{p.item}</span>
                              <span className="text-[10px] opacity-60">×{p.pathway_support}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {(s8.modifiable_factors || []).length > 0 && (
                      <div>
                        <div className={`text-[11px] uppercase font-bold tracking-widest mb-1.5 ${isDark ? "text-amber-400" : "text-amber-600"}`}>
                          {t.step8_mod}
                        </div>
                        <ul className="space-y-1">
                          {(s8.modifiable_factors || []).map((m, i) => (
                            <li key={i} className="text-[12px] flex items-center justify-between gap-2">
                              <span className="flex items-start gap-1.5"><span className="text-amber-500">▸</span>{m.item}</span>
                              <span className="text-[10px] opacity-60">×{m.pathway_support}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* iter857 — Reverse view: per-pathway food breakdown */}
                  {(s8.pathway_food_map || []).length > 0 && (
                    <PathwayReverseView
                      items={s8.pathway_food_map}
                      isDark={isDark}
                      title={t.step8_reverse_title}
                      subtitle={t.step8_reverse_sub}
                      foodsLbl={t.step8_reverse_foods_lbl}
                      intrLbl={t.step8_reverse_intr_lbl}
                      prevLbl={t.step8_reverse_prev_lbl}
                      modLbl={t.step8_reverse_mod_lbl}
                      ctdLbl={t.step8_ctd_lbl}
                      ctdHelp={t.step8_ctd_help}
                      explainHint={t.step8_explain_hint}
                      explainLoading={t.step8_explain_loading}
                      onOpenRecipes={setRecipeFocusFood}
                      API={API}
                      lang={lang}
                    />
                  )}

                  {/* iter858 — Data provenance footer */}
                  <div
                    className={`mt-3 p-2.5 rounded-lg text-[10px] leading-relaxed ${isDark ? "bg-slate-800/40 text-slate-400 border border-slate-700/50" : "bg-slate-50 text-slate-500 border border-slate-200"}`}
                    data-testid="btre-step8-sources"
                  >
                    {t.step8_sources_footer}
                  </div>
                </div>
              );
            })()}
          </SectionBlock>

          {/* STEP 9 — Dietary drivers NIS/DIL/UIS (iter832) */}
          <SectionBlock t={t.step9} cardCls={cardCls} testid="btre-step9">
            <p className={`text-[11px] mb-3 leading-relaxed ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {t.step9_sub}
            </p>
            {(() => {
              const s9 = plan.step9_dietary_drivers || {};
              if (!s9 || Object.keys(s9).length === 0) {
                return (
                  <div className={`text-xs p-3 rounded-lg italic ${isDark ? "bg-slate-800/40 text-slate-400" : "bg-slate-50 text-slate-500"}`} data-testid="btre-step9-empty">
                    {t.step9_empty}
                  </div>
                );
              }
              const win = dailyMetrics?.window || {};
              // iter870 — attach CI to NIS + DIL scores (UIS not MC-modelled).
              const scores = [
                { key: "nis", label: "NIS", avg: s9.nis_avg, target: s9.target_nis, high: s9.high_nis, sensitive: s9.sensitive_pathway_counts?.nis || 0, color: "emerald",
                  ci: win.n_meals > 0 ? win.nis_pct_ci95 : null, unit: "%" },
                { key: "dil", label: "DIL", avg: s9.dil_avg, target: s9.target_dil, high: s9.high_dil, sensitive: s9.sensitive_pathway_counts?.dil || 0, color: "orange",
                  ci: win.n_meals > 0 ? win.dil_g_ci95 : null, unit: "g" },
                { key: "uis", label: "UIS", avg: s9.uis_avg, target: s9.target_uis, high: s9.high_uis, sensitive: s9.sensitive_pathway_counts?.uis || 0, color: "rose",
                  ci: null, unit: "%" },
              ];
              return (
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-2">
                    {scores.map((s) => {
                      const highVal = s.high || 1;
                      const avgPct = Math.min(100, (s.avg / highVal) * 100);
                      const ciLoPct = s.ci ? Math.min(100, Math.max(0, (s.ci[0] / highVal) * 100)) : null;
                      const ciHiPct = s.ci ? Math.min(100, Math.max(0, (s.ci[1] / highVal) * 100)) : null;
                      return (
                        <div key={s.key} className={`p-3 rounded-xl border ${isDark ? "border-white/10" : "border-gray-100"}`} data-testid={`btre-step9-${s.key}`}>
                          <div className="flex items-baseline justify-between">
                            <span className={`font-extrabold text-lg text-${s.color}-500`}>{s.label}</span>
                            <span className="text-xs opacity-60">цел ≤ {s.target}</span>
                          </div>
                          <div className="text-2xl font-black mt-1">{s.avg}</div>
                          {/* iter870 — Progress bar with CI band overlay */}
                          <div className="relative mt-1.5 h-1.5 w-full rounded-full bg-black/10 dark:bg-white/10 overflow-hidden">
                            {s.ci && (
                              <div
                                className={`absolute top-0 h-full bg-${s.color}-500/25`}
                                style={{ left: `${ciLoPct}%`, width: `${Math.max(0.5, ciHiPct - ciLoPct)}%` }}
                                data-testid={`btre-step9-${s.key}-ci-band`}
                                title={`95% CI (Monte Carlo, 14д): ${s.ci[0].toFixed(1)}–${s.ci[1].toFixed(1)}${s.unit}`}
                              />
                            )}
                            <div className={`absolute top-0 h-full bg-${s.color}-500`} style={{ width: `${avgPct}%` }} />
                          </div>
                          {s.ci && (
                            <div className={`text-[9.5px] mt-1 ${isDark ? "text-slate-400" : "text-slate-500"}`} data-testid={`btre-step9-${s.key}-ci-text`}>
                              95% CI: {s.ci[0].toFixed(1)}–{s.ci[1].toFixed(1)}{s.unit}
                            </div>
                          )}
                          <div className="text-[10px] opacity-60 mt-1">{s.sensitive} чувств. пътя</div>
                        </div>
                      );
                    })}
                  </div>
                  {(s9.pathways_boosted || []).length > 0 && (
                    <div>
                      <div className={`text-[11px] uppercase font-bold tracking-widest mb-1.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                        {t.step9_boosted}
                      </div>
                      <div className="flex flex-wrap gap-1.5" data-testid="btre-step9-boosted">
                        {(s9.pathways_boosted || []).map((b, i) => (
                          <a
                            key={i}
                            href={b.kegg ? `https://www.kegg.jp/pathway/${b.kegg}` : "#"}
                            target={b.kegg ? "_blank" : undefined}
                            rel="noopener noreferrer"
                            title={b.kegg ? `KEGG: ${b.kegg}` : b.pathway}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-rose-100 text-rose-800 dark:bg-rose-500/20 dark:text-rose-300 hover:bg-rose-200 dark:hover:bg-rose-500/30 transition-colors"
                          >
                            {b.name_bg}
                            <span className="text-[10px] opacity-70">+{(b.delta * 100).toFixed(1)}%</span>
                            {b.kegg && <ExternalLink className="w-2.5 h-2.5 opacity-60" />}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </SectionBlock>

          {/* STEP 10 — Circadian Program (iter859) + chronotype personalisation (iter860) */}
          <SectionBlock t={t.step10} cardCls={cardCls} testid="btre-step10">
            <p className={`text-[11px] mb-3 leading-relaxed ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {t.step10_sub}
            </p>

            {/* Chronotype selector — persists to localStorage + re-fetches BTRE */}
            <div
              className={`mb-3 p-3 rounded-lg border ${isDark ? "border-violet-500/20 bg-violet-500/5" : "border-violet-200 bg-violet-50/50"}`}
              data-testid="btre-step10-chronotype-selector"
            >
              <div className={`text-[11px] uppercase font-bold tracking-widest mb-2 flex items-center gap-1.5 ${isDark ? "text-violet-300" : "text-violet-700"}`}>
                <Clock className="w-3.5 h-3.5" /> {t.step10_chronotype_lbl}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { key: "lark",     label: t.step10_lark,     icon: Sunrise, offset: "±0h" },
                  { key: "balanced", label: t.step10_balanced, icon: Sun,     offset: "+1h" },
                  { key: "owl",      label: t.step10_owl,      icon: Moon,    offset: "+2h" },
                ].map(({ key, label, icon: Icn, offset }) => {
                  const active = chronotype === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setChronotype(key)}
                      data-testid={`btre-chronotype-${key}`}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold transition-colors ${
                        active
                          ? (isDark ? "bg-violet-500 text-white" : "bg-violet-600 text-white")
                          : (isDark ? "bg-white/[0.06] text-slate-300 hover:bg-white/10" : "bg-white text-slate-700 hover:bg-violet-100 border border-violet-200")
                      }`}
                    >
                      <Icn className="w-3.5 h-3.5" /> {label}
                      <span className={`text-[9px] font-normal ${active ? "opacity-70" : "opacity-50"}`}>{offset}</span>
                    </button>
                  );
                })}
              </div>
              <p className={`text-[10px] mt-2 italic ${isDark ? "text-violet-300/70" : "text-violet-700/70"}`}>
                {t.step10_chronotype_hint}
              </p>

              {/* iter861 — Auto-detected chronotype banner (from sleep-twin) */}
              {autoChrono?.detected &&
                autoChrono.detected !== chronotype &&
                ["moderate", "high"].includes(autoChrono.confidence) &&
                !autoDismissed && (
                <div
                  className={`mt-3 rounded-lg border-l-4 p-2.5 ${isDark ? "border-indigo-400 bg-indigo-500/10" : "border-indigo-500 bg-indigo-50"}`}
                  data-testid="btre-chronotype-auto-banner"
                >
                  <div className={`text-[11px] font-bold flex items-start gap-1.5 ${isDark ? "text-indigo-200" : "text-indigo-900"}`}>
                    <Moon className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>
                      {t.step10_auto_detected}: <b>{
                        autoChrono.detected === "lark" ? t.step10_lark :
                        autoChrono.detected === "owl"  ? t.step10_owl :
                        t.step10_balanced
                      }</b>
                      {autoChrono.confidence === "high" && (
                        <span className={`ml-1 inline-flex items-center px-1.5 py-0 rounded-full text-[9px] font-bold ${isDark ? "bg-emerald-500/25 text-emerald-200" : "bg-emerald-100 text-emerald-800"}`}>
                          high confidence
                        </span>
                      )}
                    </span>
                  </div>
                  <div className={`text-[10px] mt-1 ${isDark ? "text-indigo-300/80" : "text-indigo-700"}`}>
                    <b>{t.step10_auto_evidence}:</b> {autoChrono.evidence_bg}
                    {autoChrono.suggested_wake_time && (
                      <span className="ml-1 opacity-80">· wake ~{autoChrono.suggested_wake_time}</span>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <button
                      type="button"
                      onClick={applyAutoChrono}
                      data-testid="btre-chronotype-auto-apply"
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10.5px] font-bold ${isDark ? "bg-indigo-500 hover:bg-indigo-400 text-white" : "bg-indigo-600 hover:bg-indigo-700 text-white"}`}
                    >
                      <Sparkles className="w-3 h-3" /> {t.step10_auto_apply}
                    </button>
                    <button
                      type="button"
                      onClick={dismissAutoChrono}
                      data-testid="btre-chronotype-auto-dismiss"
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10.5px] font-semibold ${isDark ? "bg-white/[0.06] hover:bg-white/10 text-slate-300" : "bg-white hover:bg-slate-100 text-slate-600 border border-slate-200"}`}
                    >
                      {t.step10_auto_reject}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {(() => {
              const s10 = plan.step10_circadian_program || {};
              if (!s10.windows || s10.windows.length === 0) {
                return (
                  <div className={`text-xs p-3 rounded-lg italic ${isDark ? "bg-slate-800/40 text-slate-400" : "bg-slate-50 text-slate-500"}`} data-testid="btre-step10-empty">
                    {t.step10_empty}
                  </div>
                );
              }
              return (
                <>
                  <CircadianProgramView
                    data={s10}
                    isDark={isDark}
                    labels={{
                      biology:      t.step10_biology,
                      foods:        t.step10_foods_lbl,
                      avoid:        t.step10_avoid_lbl,
                      supps:        t.step10_supps_lbl,
                      intr:         t.step10_intr_lbl,
                      withFat:      t.step10_with_fat,
                      constraints:  t.step10_constraints_lbl,
                      unassigned:   t.step10_unassigned_lbl,
                      pathwaysLbl:  t.step10_pathways_lbl,
                    }}
                  />

                  {/* iter860 — Physician handoff PDF export */}
                  <button
                    type="button"
                    onClick={downloadPhysicianPdf}
                    disabled={pdfBusy}
                    data-testid="btre-physician-pdf-btn"
                    className={`mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-[12px] font-bold transition-colors ${
                      pdfBusy
                        ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                        : "bg-violet-600 hover:bg-violet-700 text-white shadow-md hover:shadow-lg"
                    }`}
                  >
                    {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
                    {pdfBusy ? t.physician_pdf_busy : t.physician_pdf}
                  </button>
                </>
              );
            })()}
          </SectionBlock>

          {/* STEP 11 — Menu Suggestions + AI 7-day menu (iter863) */}
          <SectionBlock t={t.step11} cardCls={cardCls} testid="btre-step11">
            <p className={`text-[11px] mb-3 leading-relaxed ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {t.step11_sub}
            </p>
            {(() => {
              const s11 = plan.step11_menu_suggestions || {};
              const perSlot = s11.per_slot || {};
              const slots = ["morning", "late_morning", "lunch", "evening", "night"];
              const getMeals = (slot) => slotOverrides[slot] || perSlot[slot] || [];
              const anyMeals = slots.some(s => getMeals(s).length > 0);
              if (!anyMeals) {
                return (
                  <div className={`text-xs p-3 rounded-lg italic ${isDark ? "bg-slate-800/40 text-slate-400" : "bg-slate-50 text-slate-500"}`}>
                    {t.step10_empty}
                  </div>
                );
              }
              const slotLabels = {
                morning: "☀️ Сутрин", late_morning: "🌤️ Късна сутрин",
                lunch: "🍽️ Обяд", evening: "🌆 Вечер", night: "🌙 Нощ",
              };
              return (
                <div className="space-y-3" data-testid="btre-step11-per-slot">
                  {/* iter869 — Daily NIS/DIL warning banner + Monte Carlo CI */}
                  {dailyMetrics?.today && (dailyMetrics.today.meal_count > 0) && (() => {
                    const td = dailyMetrics.today;
                    const win = dailyMetrics.window || {};
                    const overAny = td.dil_over || td.nis_over;
                    const cls = overAny
                      ? (isDark ? "bg-rose-500/15 border-rose-500/40 text-rose-200" : "bg-rose-50 border-rose-300 text-rose-800")
                      : (isDark ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-200" : "bg-emerald-50 border-emerald-200 text-emerald-800");
                    return (
                      <div className={`rounded-xl border p-3 ${cls}`} data-testid="btre-step11-daily-banner">
                        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                          <div className="font-bold text-sm" data-testid="btre-step11-daily-status">
                            {td.dil_over && !td.nis_over && t.step11_daily_over_dil}
                            {td.nis_over && !td.dil_over && t.step11_daily_over_nis}
                            {td.dil_over && td.nis_over && `${t.step11_daily_over_dil} · ${t.step11_daily_over_nis}`}
                            {td.safe && t.step11_daily_safe}
                          </div>
                          <div className="text-[11px]">
                            <span className="font-semibold" data-testid="btre-step11-today-nis">
                              NIS днес: {td.nis_pct_avg.toFixed(1)}%
                            </span>
                            <span className="opacity-70"> / лимит {td.limits.nis_pct}%</span>
                          </div>
                          <div className="text-[11px]">
                            <span className="font-semibold" data-testid="btre-step11-today-dil">
                              DIL днес: {td.dil_g.toFixed(1)} g
                            </span>
                            <span className="opacity-70"> / лимит {td.limits.dil_g} g</span>
                          </div>
                        </div>
                        {win.n_meals > 0 && (
                          <div className={`text-[10.5px] mt-1 opacity-80`} data-testid="btre-step11-ci-window">
                            {t.step11_daily_ci}: NIS {win.nis_pct_mean.toFixed(1)}% [
                            {win.nis_pct_ci95[0].toFixed(1)}–{win.nis_pct_ci95[1].toFixed(1)}%] ·
                            DIL {win.dil_g_mean.toFixed(1)} g [
                            {win.dil_g_ci95[0].toFixed(1)}–{win.dil_g_ci95[1].toFixed(1)} g]
                            <span className="opacity-70"> · n={win.n_meals} meals, {win.iterations} MC iter.</span>
                          </div>
                        )}
                        {/* iter870 — Per-slot CI breakdown (only slots with data) */}
                        {dailyMetrics.by_slot && (() => {
                          const slotLabels = { morning: "Сутрин", late_morning: "Късна", lunch: "Обяд", evening: "Вечер", night: "Нощ" };
                          const nonEmpty = Object.entries(dailyMetrics.by_slot).filter(([_, v]) => v.n_meals > 0);
                          if (!nonEmpty.length) return null;
                          return (
                            <div className={`text-[10.5px] mt-1 opacity-80`} data-testid="btre-step11-slot-ci">
                              По slot (NIS%): {nonEmpty.map(([slot, v]) => (
                                <span key={slot} className="mr-2 whitespace-nowrap">
                                  <span className="opacity-70">{slotLabels[slot]}:</span>{" "}
                                  {v.nis_pct_mean.toFixed(1)} [{v.nis_pct_ci95[0].toFixed(1)}–{v.nis_pct_ci95[1].toFixed(1)}]
                                </span>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })()}

                  {/* iter867 — Global "Prefer lowest NIS" toggle */}
                  <div className="flex items-center justify-end">
                    <button
                      type="button"
                      onClick={toggleLowNisMode}
                      data-testid="btre-step11-low-nis-toggle"
                      className={`px-2.5 py-1 rounded-full text-[10.5px] font-semibold transition ${
                        lowNisMode
                          ? "bg-emerald-600 text-white"
                          : isDark
                            ? "bg-white/[0.06] text-slate-300 hover:bg-white/[0.1]"
                            : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                      }`}
                    >
                      {t.step11_low_nis_toggle}
                    </button>
                  </div>
                  {slots.map(slot => {
                    const meals = getMeals(slot);
                    if (!meals.length) return null;
                    return (
                      <div key={slot} className={`rounded-xl border p-3 ${isDark ? "border-white/10 bg-slate-900/30" : "border-gray-200 bg-white/80"}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className={`text-[12px] font-extrabold ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
                            {slotLabels[slot]}
                          </div>
                          <button
                            type="button"
                            onClick={() => reshuffleSlot(slot)}
                            disabled={reshufflingSlot === slot}
                            data-testid={`btre-step11-reshuffle-${slot}`}
                            className={`px-2 py-0.5 rounded-full text-[10px] font-semibold transition ${
                              reshufflingSlot === slot
                                ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                                : isDark
                                  ? "bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30"
                                  : "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                            }`}
                          >
                            {reshufflingSlot === slot ? t.step11_reshuffling : t.step11_reshuffle}
                          </button>
                        </div>
                        <div className="grid gap-2 md:grid-cols-2">
                          {meals.map(m => (
                            <div
                              key={m.id}
                              className={`relative rounded-lg p-2.5 border ${isDark ? "border-white/[0.06] bg-white/[0.03]" : "border-slate-200 bg-slate-50"}`}
                              data-testid={`btre-step11-meal-${m.id}`}
                            >
                              <div className={`text-[12px] font-bold ${isDark ? "text-slate-100" : "text-slate-900"}`}>
                                {m.name_bg}
                              </div>
                              <div className={`text-[10px] mt-0.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                                {m.ingredients?.join(" · ")}
                              </div>
                              <div className="mt-1.5 flex flex-wrap gap-1 text-[9.5px]">
                                <span className={`px-1.5 py-0.5 rounded-full ${isDark ? "bg-white/[0.06] text-slate-300" : "bg-white text-slate-700 border border-slate-200"}`}>
                                  {m.kcal_approx} {t.step11_kcal}
                                </span>
                                <span className={`px-1.5 py-0.5 rounded-full ${isDark ? "bg-white/[0.06] text-slate-300" : "bg-white text-slate-700 border border-slate-200"}`}>
                                  {m.prep_min} {t.step11_prep}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => setWhatIfMealId(whatIfMealId === m.id ? null : m.id)}
                                  data-testid={`btre-step11-whatif-btn-${m.id}`}
                                  className={`px-1.5 py-0.5 rounded-full font-semibold cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-emerald-400 transition ${
                                    (m.nis_pct ?? m.nis ?? 0) <= 35
                                      ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300"
                                      : (m.nis_pct ?? m.nis ?? 0) <= 55
                                      ? "bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300"
                                      : "bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-300"
                                  }`}
                                  title="Nutramilo Insulin Score (клик за What-If калкулатор)"
                                >
                                  NIS {m.nis_pct ?? m.nis ?? "?"}%
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setWhatIfMealId(whatIfMealId === m.id ? null : m.id)}
                                  data-testid={`btre-step11-whatif-btn-dil-${m.id}`}
                                  className={`px-1.5 py-0.5 rounded-full font-semibold cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-emerald-400 transition ${
                                    (m.dil_g ?? m.dil ?? 0) <= 20
                                      ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300"
                                      : (m.dil_g ?? m.dil ?? 0) <= 40
                                      ? "bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300"
                                      : "bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-300"
                                  }`}
                                  title="Dietary Insulin Load (клик за What-If калкулатор)"
                                >
                                  DIL {m.dil_g ?? m.dil ?? "?"}g
                                </button>
                                <span className={`px-1.5 py-0.5 rounded-full ${isDark ? "bg-white/[0.04] text-slate-400" : "bg-slate-100 text-slate-600"}`}
                                      title="Гликемичен индекс (информативно)">
                                  {m.gi_class === "low" ? t.step11_gi_low : m.gi_class === "moderate" ? t.step11_gi_moderate : t.step11_gi_high}
                                </span>
                                {m.match?.pathway_overlap > 0 && (
                                  <span
                                    className="px-1.5 py-0.5 rounded-full font-semibold bg-indigo-100 text-indigo-800 dark:bg-indigo-500/20 dark:text-indigo-300"
                                    title={m.match.pathway_overlap_ids?.join(", ") || ""}
                                  >
                                    ✓ {m.match.pathway_overlap} {t.step11_overlap}
                                  </span>
                                )}
                              </div>
                              <a
                                href={`/dashboard/diary?focus=plate-simulator&prefill=${encodeURIComponent(m.name_bg)}#plate-simulator-anchor`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`inline-flex items-center gap-1 mt-1.5 text-[9.5px] font-semibold ${isDark ? "text-emerald-300 hover:text-emerald-200" : "text-emerald-700 hover:text-emerald-900"}`}
                              >
                                {t.step11_recipe_link}
                              </a>
                              {whatIfMealId === m.id && (
                                <div className="absolute z-30 top-full left-0 mt-1">
                                  <WhatIfNisDilCalculator
                                    baseline={m}
                                    isDark={isDark}
                                    apiBase={API}
                                    onClose={() => setWhatIfMealId(null)}
                                  />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}

                  {/* AI 7-day menu builder */}
                  <div className={`rounded-xl border-l-4 p-3 ${isDark ? "border-fuchsia-400 bg-fuchsia-500/10" : "border-fuchsia-500 bg-fuchsia-50"}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className={`w-4 h-4 ${isDark ? "text-fuchsia-300" : "text-fuchsia-600"}`} />
                      <span className={`text-[12px] font-bold ${isDark ? "text-fuchsia-200" : "text-fuchsia-900"}`}>
                        {weekMenu ? t.step11_ai_regenerate : t.step11_ai_btn}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={generateWeekMenu}
                      disabled={weekMenuBusy}
                      data-testid="btre-step11-ai-btn"
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold ${weekMenuBusy ? "bg-slate-300 text-slate-500 cursor-not-allowed" : "bg-fuchsia-600 hover:bg-fuchsia-700 text-white"}`}
                    >
                      {weekMenuBusy ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" /> {t.step11_ai_busy}</>) : (<><Sparkles className="w-3.5 h-3.5" /> {weekMenu ? t.step11_ai_regenerate : t.step11_ai_btn}</>)}
                    </button>
                    {weekMenuErr && (
                      <div className="mt-2 text-[10px] text-red-500">{weekMenuErr}</div>
                    )}
                    {weekMenu?.days && (
                      <div className="mt-3 space-y-2" data-testid="btre-step11-week-view">
                        {weekMenu.days.map((day, di) => (
                          <div key={di} className={`rounded-lg p-2 ${isDark ? "bg-slate-900/40" : "bg-white/80"} border ${isDark ? "border-white/10" : "border-slate-200"}`}>
                            <div className={`text-[11px] font-bold mb-1 ${isDark ? "text-fuchsia-200" : "text-fuchsia-800"}`}>{day.day}</div>
                            <div className="grid gap-1 sm:grid-cols-5 text-[10px]">
                              {["morning", "late_morning", "lunch", "evening", "night"].map(sl => {
                                const meal = day.slots?.[sl];
                                if (!meal) return <div key={sl} className="opacity-40">—</div>;
                                return (
                                  <div key={sl} className={`p-1 rounded ${isDark ? "bg-white/[0.04]" : "bg-slate-50"}`}>
                                    <div className={`font-semibold ${isDark ? "text-slate-200" : "text-slate-700"}`}>
                                      {slotLabels[sl].split(" ")[1]}
                                    </div>
                                    <div className={`${isDark ? "text-slate-400" : "text-slate-600"}`}>
                                      {meal.name_bg}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            {day.notes_bg && (
                              <p className={`text-[9.5px] mt-1 italic ${isDark ? "text-slate-400" : "text-slate-500"}`}>{day.notes_bg}</p>
                            )}
                          </div>
                        ))}
                        {weekMenu.summary_bg && (
                          <p className={`text-[11px] p-2 rounded-lg ${isDark ? "bg-fuchsia-500/10 text-fuchsia-200" : "bg-fuchsia-50 text-fuchsia-900"}`}>
                            {weekMenu.summary_bg}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {s11.disclaimer && (
                    <p className={`text-[10px] italic ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                      {s11.disclaimer}
                    </p>
                  )}
                </div>
              );
            })()}
          </SectionBlock>
          <div className={`${cardCls} p-5`} data-testid="btre-ai-section">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-teal-500" />
              <h4 className="text-base font-bold">{t.enrich.replace("✨ ", "")}</h4>
              {enrich?.cached && (
                <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                  {t.cached}
                </span>
              )}
            </div>
            {enrich?.narrative ? (
              <div className={`whitespace-pre-wrap text-sm leading-relaxed ${isDark ? "text-gray-200" : "text-gray-800"}`} data-testid="btre-ai-text">
                {enrich.narrative}
              </div>
            ) : enrich?.error ? (
              <div className="text-sm text-red-500" data-testid="btre-ai-error">{enrich.error}</div>
            ) : (
              <div className="text-sm opacity-60 italic">{t.noAi}</div>
            )}
            {enrich?.narrative && (
              <button
                onClick={() => runEnrich(true)}
                disabled={enrichLoading}
                className="mt-3 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300 hover:opacity-90 flex items-center gap-1.5"
                data-testid="btre-ai-refresh-btn"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${enrichLoading ? "animate-spin" : ""}`} /> {t.forceRefresh}
              </button>
            )}
          </div>
        </>
      )}

      {/* iter834 — Recipe deep-link from Step 8 food chips */}
      <PathwayMenuModal
        open={!!recipeFocusFood}
        onClose={() => setRecipeFocusFood(null)}
        isDark={isDark}
        focusFood={recipeFocusFood}
      />
    </div>
  );
}


// iter917 — Evidence-backed roadmap item. Renders both the legacy
// string shape and the new `{text, evidence}` shape with a compact
// citation chip row (KEGG · p-value · source links).
function RoadmapItem({ item, isDark }) {
  const [showEvidence, setShowEvidence] = React.useState(false);
  if (typeof item === "string") {
    return (
      <li className="text-[12px] flex items-start gap-1.5" data-testid="roadmap-item-template">
        <span className="text-emerald-500">▸</span>
        <span>{item}</span>
      </li>
    );
  }
  const ev = item.evidence || {};
  const kegg = (ev.pathway_kegg || "").replace("KEGG:", "");
  const srcs = ev.sources || [];
  return (
    <li className="text-[12px]" data-testid="roadmap-item-evidence">
      <div className="flex items-start gap-1.5">
        <span className="text-emerald-500 mt-0.5">▸</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <span className="font-medium">{item.text}</span>
            <button
              type="button"
              onClick={() => setShowEvidence((v) => !v)}
              data-testid="roadmap-item-evidence-toggle"
              className={`shrink-0 inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
                isDark ? "bg-emerald-900/40 text-emerald-300 hover:bg-emerald-900/60"
                       : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
              }`}
              title="Виж източниците"
            >
              <ShieldCheck className="w-2.5 h-2.5" />
              {ev.p_value_label ? "PMID-ready" : "evidence"}
            </button>
          </div>
          {showEvidence && (
            <div
              data-testid="roadmap-item-evidence-panel"
              className={`mt-1 p-2 rounded-lg text-[10px] ${
                isDark ? "bg-slate-900/60 border border-slate-800" : "bg-slate-50 border border-slate-200"
              }`}
            >
              <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                {kegg && (
                  <span className={isDark ? "text-slate-300" : "text-slate-700"}>
                    <b>Pathway:</b> {kegg}
                    {ev.pathway_name && ` (${ev.pathway_name})`}
                  </span>
                )}
                {ev.p_value_label && (
                  <span className={isDark ? "text-emerald-300" : "text-emerald-700"}>
                    <b>Сила на доказателството:</b> {ev.p_value_label}
                  </span>
                )}
                {ev.driver_id && (
                  <span className={isDark ? "text-slate-400" : "text-slate-500"}>
                    <b>Драйвер:</b> {ev.driver_id}
                  </span>
                )}
              </div>
              {srcs.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {srcs.map((s, i) => (
                    <a
                      key={i}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`roadmap-source-${s.kind}`}
                      className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-semibold ${
                        isDark ? "bg-indigo-900/40 hover:bg-indigo-900/60 text-indigo-200"
                               : "bg-indigo-100 hover:bg-indigo-200 text-indigo-700"
                      }`}
                    >
                      {s.label}
                      <ExternalLink className="w-2 h-2 opacity-60" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}


function SectionBlock({ t, children, cardCls, testid }) {
  return (
    <div className={`${cardCls} p-5`} data-testid={testid}>
      <h4 className="text-sm font-extrabold uppercase tracking-wide opacity-70 mb-3">{t}</h4>
      {children}
    </div>
  );
}

// ─── iter857 — Expandable chips revealing pathway provenance ───

function FoodChipExpandable({ food, index, isDark, revealLabel, hideLabel, onOpenRecipes }) {
  const [open, setOpen] = useState(false);
  const pws = food.supporting_pathways || [];
  return (
    <div
      className={`rounded-xl border ${isDark ? "border-emerald-500/20 bg-emerald-500/5" : "border-emerald-200 bg-emerald-50/50"}`}
      data-testid={`btre-step8-food-${index}`}
    >
      <div className="flex items-center gap-2 p-2 flex-wrap">
        <Apple className={`w-4 h-4 ${isDark ? "text-emerald-300" : "text-emerald-700"}`} />
        <span className={`font-semibold text-[12px] ${isDark ? "text-emerald-100" : "text-emerald-900"}`}>
          {food.item}
        </span>
        <span
          className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold"
          title={`Подпомага ${food.pathway_support} от вашите активни пътища`}
        >
          {food.pathway_support}
        </span>
        <div className="ml-auto flex items-center gap-1">
          {pws.length > 0 && (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              data-testid={`btre-step8-food-toggle-${index}`}
              className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full bg-white/70 text-emerald-700 dark:bg-slate-800/60 dark:text-emerald-200 hover:bg-emerald-100 dark:hover:bg-emerald-500/30 transition-colors"
            >
              {open ? hideLabel : revealLabel}
              <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
          )}
          <button
            type="button"
            onClick={onOpenRecipes}
            data-testid={`btre-step8-food-recipes-${index}`}
            aria-label={`Рецепти с ${food.item}`}
            title={`📖 Отвори 7-дневно меню с рецепти, съдържащи «${food.item}»`}
            className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/70 hover:bg-emerald-600 hover:text-white text-emerald-700 dark:bg-slate-800/60 dark:text-emerald-200 transition-colors"
          >
            <BookOpen className="w-3 h-3" strokeWidth={2.5} />
          </button>
        </div>
      </div>
      {open && pws.length > 0 && (
        <div
          className={`px-3 pb-2.5 pt-1 border-t ${isDark ? "border-emerald-500/10" : "border-emerald-200/60"}`}
          data-testid={`btre-step8-food-provenance-${index}`}
        >
          <div className={`text-[10px] uppercase tracking-wider font-bold mb-1.5 ${isDark ? "text-emerald-300/70" : "text-emerald-700/70"}`}>
            Подпомагани пътища ({pws.length})
          </div>
          <div className="flex flex-wrap gap-1">
            {pws.map((pw) => (
              <a
                key={pw.id}
                href={pw.kegg ? `https://www.kegg.jp/pathway/${pw.kegg}` : "#"}
                target={pw.kegg ? "_blank" : undefined}
                rel="noopener noreferrer"
                title={pw.kegg ? `KEGG: ${pw.kegg}` : pw.id}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-500/20 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-500/30 transition-colors"
              >
                {pw.name_bg}
                {pw.kegg && <ExternalLink className="w-2 h-2 opacity-60" />}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function InterventionChipExpandable({ item, index, isDark, revealLabel, hideLabel }) {
  const [open, setOpen] = useState(false);
  const pws = item.supporting_pathways || [];
  return (
    <div
      className={`rounded-xl border ${isDark ? "border-teal-500/20 bg-teal-500/5" : "border-teal-200 bg-teal-50/50"}`}
      data-testid={`btre-step8-intr-${index}`}
    >
      <div className="flex items-center gap-2 p-2 flex-wrap">
        <Activity className={`w-4 h-4 ${isDark ? "text-teal-300" : "text-teal-700"}`} />
        <span className={`font-semibold text-[12px] ${isDark ? "text-teal-100" : "text-teal-900"}`}>
          {item.item}
        </span>
        <span
          className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-teal-600 text-white text-[10px] font-bold"
          title={`Подпомага ${item.pathway_support} пътя`}
        >
          {item.pathway_support}
        </span>
        {pws.length > 0 && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid={`btre-step8-intr-toggle-${index}`}
            className="ml-auto inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full bg-white/70 text-teal-700 dark:bg-slate-800/60 dark:text-teal-200 hover:bg-teal-100 dark:hover:bg-teal-500/30 transition-colors"
          >
            {open ? hideLabel : revealLabel}
            <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        )}
      </div>
      {open && pws.length > 0 && (
        <div
          className={`px-3 pb-2.5 pt-1 border-t ${isDark ? "border-teal-500/10" : "border-teal-200/60"}`}
          data-testid={`btre-step8-intr-provenance-${index}`}
        >
          <div className={`text-[10px] uppercase tracking-wider font-bold mb-1.5 ${isDark ? "text-teal-300/70" : "text-teal-700/70"}`}>
            Подпомагани пътища ({pws.length})
          </div>
          <div className="flex flex-wrap gap-1">
            {pws.map((pw) => (
              <a
                key={pw.id}
                href={pw.kegg ? `https://www.kegg.jp/pathway/${pw.kegg}` : "#"}
                target={pw.kegg ? "_blank" : undefined}
                rel="noopener noreferrer"
                title={pw.kegg ? `KEGG: ${pw.kegg}` : pw.id}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-500/20 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-500/30 transition-colors"
              >
                {pw.name_bg}
                {pw.kegg && <ExternalLink className="w-2 h-2 opacity-60" />}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PathwayReverseView({
  items, isDark, title, subtitle, foodsLbl, intrLbl, prevLbl, modLbl,
  ctdLbl, ctdHelp, explainHint, explainLoading, onOpenRecipes,
  API, lang,
}) {
  const [openAll, setOpenAll] = useState(false);
  const shown = openAll ? items : items.slice(0, 5);
  return (
    <div
      className={`mt-2 rounded-xl border ${isDark ? "border-indigo-500/20 bg-indigo-500/5" : "border-indigo-200 bg-indigo-50/40"} p-4`}
      data-testid="btre-step8-reverse-view"
    >
      <div className="flex items-start gap-2 mb-1">
        <div className="flex-1">
          <div className={`text-sm font-bold ${isDark ? "text-indigo-200" : "text-indigo-800"}`}>{title}</div>
          <p className={`text-[11px] leading-relaxed mt-0.5 ${isDark ? "text-indigo-300/70" : "text-indigo-700/70"}`}>{subtitle}</p>
        </div>
      </div>
      <div className="mt-3 space-y-2.5">
        {shown.map((pw) => (
          <div
            key={pw.id}
            className={`rounded-lg border ${isDark ? "border-white/10 bg-slate-900/40" : "border-gray-200 bg-white/80"} p-3`}
            data-testid={`btre-step8-reverse-pw-${pw.id}`}
          >
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Dna className={`w-3.5 h-3.5 ${isDark ? "text-indigo-300" : "text-indigo-600"}`} />
              <a
                href={pw.kegg ? `https://www.kegg.jp/pathway/${pw.kegg}` : "#"}
                target={pw.kegg ? "_blank" : undefined}
                rel="noopener noreferrer"
                title={pw.kegg ? `KEGG: ${pw.kegg}` : pw.id}
                className={`text-[12px] font-bold ${isDark ? "text-indigo-100 hover:text-indigo-300" : "text-indigo-900 hover:text-indigo-600"} transition-colors flex items-center gap-1`}
              >
                {pw.name_bg}
                {pw.kegg && <ExternalLink className="w-2.5 h-2.5 opacity-60" />}
              </a>
              {pw.kegg && (
                <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${isDark ? "bg-white/5 text-slate-400" : "bg-slate-100 text-slate-500"}`}>
                  {pw.kegg}
                </span>
              )}
            </div>

            {pw.foods?.length > 0 && (
              <div className="mb-1.5">
                <div className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${isDark ? "text-emerald-300/80" : "text-emerald-700/80"}`}>
                  {foodsLbl}
                </div>
                <div className="flex flex-wrap gap-1">
                  {pw.foods.map((food, i) => (
                    <FoodExplainableChip
                      key={i}
                      food={food}
                      pathwayId={pw.id}
                      pathwayName={pw.name_bg}
                      isDark={isDark}
                      lang={lang}
                      API={API}
                      hint={explainHint}
                      loading={explainLoading}
                      onOpenRecipes={onOpenRecipes}
                    />
                  ))}
                </div>
              </div>
            )}

            {pw.interventions?.length > 0 && (
              <div className="mb-1.5">
                <div className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${isDark ? "text-teal-300/80" : "text-teal-700/80"}`}>
                  {intrLbl}
                </div>
                <div className="flex flex-wrap gap-1">
                  {pw.interventions.map((iv, i) => (
                    <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-teal-100 text-teal-800 dark:bg-teal-500/20 dark:text-teal-300">
                      {iv}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* iter858 — CTD compounds acting on this pathway */}
            {pw.ctd_compounds?.length > 0 && (
              <div className="mb-1.5">
                <div
                  className={`text-[10px] uppercase tracking-wider font-bold mb-1 flex items-center gap-1 ${isDark ? "text-fuchsia-300/80" : "text-fuchsia-700/80"}`}
                  title={ctdHelp}
                >
                  <FlaskConical className="w-3 h-3" /> {ctdLbl}
                  <Info className="w-2.5 h-2.5 opacity-60" />
                </div>
                <div className="flex flex-wrap gap-1" data-testid={`btre-step8-ctd-${pw.id}`}>
                  {pw.ctd_compounds.map((c, i) => (
                    <a
                      key={i}
                      href={c.ctd_url || "#"}
                      target={c.ctd_url ? "_blank" : undefined}
                      rel="noopener noreferrer"
                      title={`CTD: ${c.chem}${c.cas ? " (CAS " + c.cas + ")" : ""} — p=${c.corr_p.toExponential(2)}`}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-500/20 dark:text-fuchsia-300 hover:bg-fuchsia-200 dark:hover:bg-fuchsia-500/30 transition-colors"
                    >
                      {c.chem}
                      <ExternalLink className="w-2 h-2 opacity-60" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-2 mt-1.5">
              {pw.prevention_focus?.length > 0 && (
                <div>
                  <div className={`text-[10px] uppercase tracking-wider font-bold mb-0.5 ${isDark ? "text-purple-300/80" : "text-purple-700/80"}`}>
                    {prevLbl}
                  </div>
                  <div className={`text-[11px] ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                    {pw.prevention_focus.join(" · ")}
                  </div>
                </div>
              )}
              {pw.modifiable_factors?.length > 0 && (
                <div>
                  <div className={`text-[10px] uppercase tracking-wider font-bold mb-0.5 ${isDark ? "text-amber-300/80" : "text-amber-700/80"}`}>
                    {modLbl}
                  </div>
                  <div className={`text-[11px] ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                    {pw.modifiable_factors.join(" · ")}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {items.length > 5 && (
        <button
          type="button"
          onClick={() => setOpenAll((v) => !v)}
          data-testid="btre-step8-reverse-toggle-all"
          className={`mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full ${isDark ? "bg-indigo-500/20 text-indigo-200 hover:bg-indigo-500/30" : "bg-indigo-100 text-indigo-800 hover:bg-indigo-200"} transition-colors`}
        >
          {openAll ? `Скрий (показани всички ${items.length})` : `Покажи всички ${items.length} пътя`}
          <ChevronDown className={`w-3 h-3 transition-transform ${openAll ? "rotate-180" : ""}`} />
        </button>
      )}
    </div>
  );
}

// iter858 — Food chip with click-to-expand AI mechanism explanation
function FoodExplainableChip({ food, pathwayId, pathwayName, isDark, lang, API, hint, loading, onOpenRecipes }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (text || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const { data } = await axios.post(
        `${API}/bhi/pathway-food-explanation`,
        { food, pathway_id: pathwayId, lang: lang || "bg" },
        { timeout: 30000 },
      );
      setText(data?.explanation || "");
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Грешка");
    } finally {
      setBusy(false);
    }
  };

  return (
    <span
      className="inline-flex flex-col items-stretch"
      data-testid={`btre-step8-food-explainable-${pathwayId}-${food}`}
    >
      <span className="inline-flex items-center gap-1 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300 overflow-hidden">
        <button
          type="button"
          onClick={() => onOpenRecipes && onOpenRecipes(food)}
          title={`📖 Рецепти с ${food}`}
          className="inline-flex items-center gap-1 pl-2 py-0.5 hover:bg-emerald-200/50 dark:hover:bg-emerald-500/30 transition-colors"
        >
          <Apple className="w-2.5 h-2.5" /> {food}
        </button>
        <button
          type="button"
          onClick={toggle}
          title={hint}
          aria-label={hint}
          data-testid={`btre-step8-food-explain-toggle-${pathwayId}-${food}`}
          className={`inline-flex items-center justify-center w-5 h-5 pr-1 hover:bg-emerald-300/50 dark:hover:bg-emerald-400/30 transition-colors ${open ? "bg-emerald-300/40 dark:bg-emerald-400/20" : ""}`}
        >
          {busy ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Info className="w-2.5 h-2.5" />}
        </button>
      </span>
      {open && (
        <div
          className={`mt-1 max-w-[420px] text-[10px] leading-relaxed p-2 rounded-md ${isDark ? "bg-slate-800/80 text-slate-200 border border-emerald-500/30" : "bg-white text-slate-700 border border-emerald-300"}`}
          data-testid={`btre-step8-food-explain-body-${pathwayId}-${food}`}
        >
          {busy && (
            <span className="inline-flex items-center gap-1 opacity-70">
              <Loader2 className="w-3 h-3 animate-spin" /> {loading}
            </span>
          )}
          {err && <span className="text-red-500">{err}</span>}
          {text && (
            <>
              <span className={`inline-block text-[9px] uppercase tracking-wider font-bold mr-1 ${isDark ? "text-emerald-300/80" : "text-emerald-700/80"}`}>
                {food} → {pathwayName}:
              </span>
              {text}
            </>
          )}
        </div>
      )}
    </span>
  );
}


// iter859 — Circadian Program (Step 10)
const SLOT_ICONS = {
  morning:      Sunrise,
  late_morning: Sun,
  lunch:        Utensils,
  evening:      Sunset,
  night:        Moon,
};

const SLOT_ACCENT = {
  morning:      { light: "bg-amber-50 border-amber-300",     dark: "bg-amber-500/10 border-amber-500/40",     text: "text-amber-700 dark:text-amber-300"      },
  late_morning: { light: "bg-yellow-50 border-yellow-300",   dark: "bg-yellow-500/10 border-yellow-500/40",   text: "text-yellow-700 dark:text-yellow-300"     },
  lunch:        { light: "bg-emerald-50 border-emerald-300", dark: "bg-emerald-500/10 border-emerald-500/40", text: "text-emerald-700 dark:text-emerald-300"   },
  evening:      { light: "bg-violet-50 border-violet-300",   dark: "bg-violet-500/10 border-violet-500/40",   text: "text-violet-700 dark:text-violet-300"     },
  night:        { light: "bg-indigo-50 border-indigo-300",   dark: "bg-indigo-500/10 border-indigo-500/40",   text: "text-indigo-700 dark:text-indigo-300"     },
};

function CircadianProgramView({ data, isDark, labels }) {
  const windows = data.windows || [];
  const constraints = data.constraints || [];
  return (
    <div className="space-y-4" data-testid="btre-step10-view">
      {/* 5 time windows */}
      <div className="grid gap-3 md:grid-cols-2">
        {windows.map((w) => {
          const Icon = SLOT_ICONS[w.slot] || Clock;
          const accent = SLOT_ACCENT[w.slot] || { light: "bg-slate-50 border-slate-300", dark: "bg-slate-500/10 border-slate-500/40", text: "text-slate-700 dark:text-slate-300" };
          const isEmpty =
            !w.foods?.length && !w.supplements?.length && !w.interventions?.length && !w.avoid_foods?.length;
          return (
            <div
              key={w.slot}
              className={`rounded-xl border p-3 ${isDark ? accent.dark : accent.light} ${isEmpty ? "opacity-70" : ""}`}
              data-testid={`btre-step10-slot-${w.slot}`}
            >
              <div className="flex items-start gap-2 mb-2">
                <Icon className={`w-5 h-5 shrink-0 mt-0.5 ${accent.text}`} />
                <div className="flex-1 min-w-0">
                  <div className={`text-[13px] font-extrabold ${accent.text}`}>{w.label_bg}</div>
                  <p className={`text-[10px] leading-relaxed mt-0.5 ${isDark ? "text-slate-300/80" : "text-slate-600"}`}>
                    <span className="font-semibold">{labels.biology}: </span>
                    {w.biology_bg}
                  </p>
                </div>
              </div>

              {w.foods?.length > 0 && (
                <div className="mb-2">
                  <div className={`text-[10px] uppercase font-bold tracking-wider mb-1 flex items-center gap-1 ${accent.text}`}>
                    <Apple className="w-3 h-3" /> {labels.foods}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {w.foods.map((f, i) => (
                      <span
                        key={i}
                        title={f.reason_bg}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${isDark ? "bg-white/10 text-slate-100" : "bg-white text-slate-800"} border ${isDark ? "border-white/10" : "border-slate-200"}`}
                        data-testid={`btre-step10-food-${w.slot}-${i}`}
                      >
                        {f.item}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {w.supplements?.length > 0 && (
                <div className="mb-2">
                  <div className={`text-[10px] uppercase font-bold tracking-wider mb-1 flex items-center gap-1 ${accent.text}`}>
                    <Pill className="w-3 h-3" /> {labels.supps}
                  </div>
                  <div className="flex flex-col gap-1">
                    {w.supplements.map((s, i) => (
                      <div
                        key={i}
                        className={`text-[10.5px] leading-snug rounded-md px-2 py-1 ${isDark ? "bg-white/[0.06] text-slate-200" : "bg-white text-slate-800"} border ${isDark ? "border-white/10" : "border-slate-200"}`}
                        data-testid={`btre-step10-supp-${w.slot}-${i}`}
                      >
                        <span className="font-semibold">{s.name_bg}</span>
                        {s.with_fat && (
                          <span className={`ml-1 inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full font-bold ${isDark ? "bg-amber-500/20 text-amber-300" : "bg-amber-100 text-amber-700"}`}>
                            🥑 {labels.withFat}
                          </span>
                        )}
                        <div className={`text-[9.5px] mt-0.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>{s.reason_bg}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {w.interventions?.length > 0 && (
                <div className="mb-2">
                  <div className={`text-[10px] uppercase font-bold tracking-wider mb-1 flex items-center gap-1 ${accent.text}`}>
                    <Activity className="w-3 h-3" /> {labels.intr}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {w.interventions.map((iv, i) => (
                      <span
                        key={i}
                        title={iv.reason_bg || undefined}
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] ${isDark ? "bg-teal-500/20 text-teal-200" : "bg-teal-100 text-teal-800"}`}
                        data-testid={`btre-step10-intr-${w.slot}-${i}`}
                      >
                        {iv.item}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {w.avoid_foods?.length > 0 && (
                <div>
                  <div className={`text-[10px] uppercase font-bold tracking-wider mb-1 flex items-center gap-1 ${isDark ? "text-red-300/80" : "text-red-600/80"}`}>
                    <Ban className="w-3 h-3" /> {labels.avoid}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {w.avoid_foods.map((av, i) => (
                      <span
                        key={i}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] ${isDark ? "bg-red-500/15 text-red-300 line-through decoration-red-400/60" : "bg-red-50 text-red-700 line-through decoration-red-500/60"}`}
                        data-testid={`btre-step10-avoid-${w.slot}-${i}`}
                      >
                        {av}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Universal chrono-constraints */}
      {constraints.length > 0 && (
        <div
          className={`rounded-xl border p-3 ${isDark ? "border-slate-700/60 bg-slate-900/40" : "border-slate-200 bg-slate-50/60"}`}
          data-testid="btre-step10-constraints"
        >
          <div className={`text-[11px] uppercase font-bold tracking-widest mb-2 flex items-center gap-1.5 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
            <Coffee className="w-3.5 h-3.5" /> {labels.constraints}
          </div>
          <ul className="space-y-1">
            {constraints.map((c, i) => (
              <li
                key={i}
                className={`text-[11px] flex items-start gap-1.5 ${isDark ? "text-slate-300" : "text-slate-700"}`}
                data-testid={`btre-step10-constraint-${i}`}
              >
                <span className={isDark ? "text-amber-400" : "text-amber-600"}>▸</span>
                <span>{c.rule_bg}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Unassigned foods (flexible) */}
      {data.unassigned_foods?.length > 0 && (
        <div className={`text-[10.5px] italic ${isDark ? "text-slate-400" : "text-slate-500"}`}>
          <span className="font-semibold">{labels.unassigned}:</span> {data.unassigned_foods.join(" · ")}
        </div>
      )}

      {/* Disclaimer */}
      {data.disclaimer && (
        <div
          className={`p-2.5 rounded-lg text-[10px] leading-relaxed border-l-4 ${isDark ? "bg-amber-500/10 border-amber-500 text-amber-200" : "bg-amber-50 border-amber-500 text-amber-800"}`}
          data-testid="btre-step10-disclaimer"
        >
          <ShieldAlert className="w-3 h-3 inline mr-1 -mt-0.5" />
          {data.disclaimer}
        </div>
      )}
    </div>
  );
}
